import React, { useEffect, useRef, useState } from 'react'
import { Button, Checkbox, ConfigProvider, Statistic } from 'antd'
import { BackButton, ChannelLabelWrapper, DashboardWrapper, DownloadLoading, LabelIcon, LabelWrapper, RowWrapper, StatisticWrapper, Title, TrendLabelWrapper, VerticalLine, VerticalLineWrapper, WhiteWrapper } from '../invoice.styled'
import { useTranslation } from 'react-i18next'
import { ageChartProps, channelChartProps, channelColors, channelLabel, channelShareChartProps, genderChartProps, trendChartProps, trendColors } from './chartConf'
import dayjs from 'dayjs'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import ChartComponent from './ChartComponent'
import { downloadPDF } from './donwload'

const mapRgbNumber = '106,159,58'
function InvoiceDashboard (props) {
  const { goBackPath } = props
  const { t: ln_t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const docRef = useRef()
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [trendingHighlight, setTrendingHighlight] = useState({})
  const [channelState, setChannelState] = useState({})

  const { data } = location?.state || {}
  const { name, startDate, endDate, dashboardDatas } = data || {}
  const { summary_custom_date, gender_custom_date, age_custom_date, city_custom_date, summary_last_month, city_last_month, trend_daily, channel_daily, channel_share } = dashboardDatas || {}
  const customDate = `${dayjs(startDate)?.format('YYYY/MM/DD')} - ${dayjs(endDate)?.format('YYYY/MM/DD')}`
  const channelLabels = channelLabel(channel_daily?.data || [])
  const channelColorMapping = channelLabels.reduce((all, i, idx) => ({ ...all, [i]: channelColors[idx % channelColors?.length] }), {})

  const statistics = [
    { key: 'inv_num', title: ln_t('invoice.quantity'), suffix: ln_t('invoice.quantityUnit') },
    { key: 'count_all', title: ln_t('invoice.transactions'), suffix: ln_t('invoice.transactionUnit') },
    { key: 'uuid', title: ln_t('invoice.totalConsumers') },
    { key: 'amount', title: ln_t('invoice.totalConsumption') }
  ]

  const getStatisticProps = (summaryData, key, originalProps) => {
    if (!summaryData?.data) {
      return { ...originalProps, value: 0 }
    }
    // handle new array format: [{rate, name, diff}] - for summary_last_month
    if (Array.isArray(summaryData.data)) {
      const item = summaryData.data.find(item => item.name === key)
      if (!item) return { ...originalProps, value: 0 }
      const { diff, rate } = item
      const isPositiveRate = rate > 0
      const diffValue = parseFloat(diff)
      const isPositiveDiff = diffValue > 0
      const diffDisplay = isPositiveDiff ? `+${diffValue.toLocaleString()}` : diffValue.toLocaleString()
      const rateDisplay = isPositiveRate ? `+${rate}%` : `${rate}%`
      const color = isPositiveRate ? '#ff4d4f' : '#52c41a'
      return {
        ...originalProps,
        suffix: '',
        value: diffDisplay,
        formatter: (val) => (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px' }}>
              {val}{originalProps.suffix}{' '}
              <span style={{ color }}>
                ({rateDisplay})
              </span>
            </div>
          </div>
        )
      }
    }
    // handle old object format: {key: value} - for summary_custom_date and fallback for summary_last_month
    const value = summaryData.data[key]
    if (value === undefined) return { ...originalProps, value: 0 }
    return {
      ...originalProps,
      value: value?.toLocaleString() || 0
    }
  }

  const getOpacity = i => i >= 15 ? 0.2 : i >= 10 ? 0.4 : i >= 5 ? 0.7 : 1
  const getSortKey = data => Object.entries(data).sort((a, b) => b[1].count - a[1].count)?.map(([key]) => key)
  const mapColorLastMonth = {}
  getSortKey(city_last_month?.data || {}).forEach((i, idx) => { mapColorLastMonth[i] = mapRgbNumber ? `rgba(${mapRgbNumber}, ${getOpacity(idx)})` : 'rgba(0,0,0,0.2)' })
  const mapColorCustom = {}
  getSortKey(city_custom_date?.data || {}).forEach((i, idx) => { mapColorCustom[i] = mapRgbNumber ? `rgba(${mapRgbNumber}, ${getOpacity(idx)})` : 'rgba(0,0,0,0.2)' })

  const handleDownloadReport = async () => {
    setDownloadLoading(true)
    await downloadPDF(docRef, name)
    setDownloadLoading(false)
  }

  const handleTrendingCheckboxChange = e => {
    const rank = 3
    const highest = e.includes('highest')
    const lowest = e.includes('lowest')
    const getRank = (key, asc) => trend_daily?.data?.map(i => Number(i[key]))?.filter(i => i !== 0)?.sort((a, b) => asc ? a - b : b - a).slice(0, rank)
    const getHighlight = key => [...highest ? getRank(key) : [], ...lowest ? getRank(key, true) : []]
    setTrendingHighlight({ inv_num: getHighlight('inv_num'), uuid: getHighlight('uuid'), amount: getHighlight('amount') })
  }

  const findTarget = (completeArray, incompleteArray) => completeArray.filter(i => !incompleteArray.includes(i))?.[0]
  const getSelected = e => {
    const prevSelected = channelState?.selected || []
    const actionIsSelect = e?.length > prevSelected?.length
    const itemTarget = actionIsSelect ? findTarget(e, prevSelected) : findTarget(prevSelected, e)
    const targetIsAll = itemTarget === 'all'
    if (targetIsAll) return actionIsSelect ? ['all', ...channelLabels] : []
    if (!actionIsSelect) return e?.filter(i => i !== 'all')
    return e?.length === channelLabels?.length ? ['all', ...e] : e
  }
  const handleChannelCheckboxChange = e => {
    const selected = getSelected(e)
    const selectedData = selected?.filter(i => i !== 'all')?.map(channel => channel_daily?.data?.map(i => i?.[channel]))?.flat()
    const highest = selectedData?.sort((a, b) => b - a)?.[0]
    setChannelState({ selected, colorMapping: channelColorMapping, highest })
  }

  useEffect(() => {
    if (channel_daily) handleChannelCheckboxChange(['all'])
  }, [channel_daily])

  const cleaningUnknown = obj => Object.entries(obj || {})?.filter(([key]) => key !== '未知')?.reduce((all, [key, val]) => ({ ...all, [key]: val }), {})
  return <>
    <BackButton>
      <ArrowLeftOutlined onClick={() => navigate(goBackPath)} /> {name}
    </BackButton>

    <DashboardWrapper>
      {downloadLoading && <DownloadLoading />}
      <WhiteWrapper ref={docRef}>
        {/* ----- Consumption Custom Date -----  */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title>{customDate} {ln_t('invoice.consumptionOverview')}</Title>
          <ConfigProvider theme={{ token: { colorPrimary: 'rgb(71, 182, 175)', colorPrimaryHover: 'rgba(71, 182, 175,.8)' } }}>
            <Button className='downloadHide' type="primary" style={{ minWidth: '140px' }} onClick={handleDownloadReport}>
              {ln_t('invoice.downloadReport')}
            </Button>
          </ConfigProvider>
        </div>
        <StatisticWrapper>
          {statistics.map(({ key, ...i }, idx) => <React.Fragment key={key}>
            <Statistic {...getStatisticProps(summary_custom_date, key, i)} />
            {idx !== statistics?.length - 1 && idx !== 1 && <VerticalLineWrapper><VerticalLine /></VerticalLineWrapper>}
          </React.Fragment>)}
        </StatisticWrapper>

        <RowWrapper>
          <div>
            <ChartComponent
              title={ln_t('invoice.consumer')}
              data={gender_custom_date?.data}
              type="bar"
              propsFunc={genderChartProps}
              useDivider={true}
            />
            <ChartComponent
              title={ln_t('invoice.consumerAge')}
              data={cleaningUnknown(age_custom_date?.data)}
              type="horizontalBar"
              propsFunc={ageChartProps}
              wrapperProps={{ style: { marginTop: '24px' } }}
              useDivider={true}
            />
          </div>
          <ChartComponent
            title={ln_t('invoice.consumptionArea')}
            data={city_custom_date?.data}
            type="map"
            propsFunc={() => ({
              idKey: 'cityLastMonth',
              statistics: Object.entries(city_custom_date?.data)?.reduce((all, [key, value]) => ({ ...all, [key]: value.percentage }), {}),
              valueObj: Object.entries(city_custom_date?.data)?.reduce((all, [key, value]) => ({ ...all, [key]: value.count }), {}),
              areaColorSetting: mapColorCustom,
              height: '620px',
              afterFix: '%'
            })}
            wrapperProps={{ fullheight: true }}
            useDivider={true}
          />
        </RowWrapper>
        <div className="page-break" style={{ visibility: 'hidden' }} />

        {/* ----- Consumer Last Month -----  */}
        <Title>
          {ln_t('invoice.latestMoM')} <br />
          <span style={{ fontSize: '16px', fontWeight: 400 }}>{ln_t('invoice.latestMoMDesc')}</span>
        </Title>
        <StatisticWrapper>
          {statistics.map(({ key, ...i }, idx) => <React.Fragment key={key}>
            <Statistic {...getStatisticProps(summary_last_month, key, i)} />
            {idx !== statistics?.length - 1 && idx !== 1 && <VerticalLineWrapper><VerticalLine /></VerticalLineWrapper>}
          </React.Fragment>)}
        </StatisticWrapper>

        {/* ----- Other Charts -----  */}
        <ChartComponent
          title={ln_t('invoice.consumptionAmountTrend')}
          data={trend_daily?.data}
          type="bar"
          propsFunc={trendChartProps}
          wrapperProps={{ fullwidth: true }}
          innerProps={{ borderless: true }}
          chartState={trendingHighlight}
          customLabel={{
            position: 'right',
            component: <ConfigProvider theme={{ token: { colorPrimary: 'rgb(71, 182, 175)', colorPrimaryHover: 'rgba(71, 182, 175,.8)' } }}>
              <TrendLabelWrapper>
                <Checkbox.Group
                  options={[{ value: 'highest', label: ln_t('invoice.showHighest3') }, { value: 'lowest', label: ln_t('invoice.showLowest3') }]}
                  style={{ marginBottom: '40px' }}
                  onChange={handleTrendingCheckboxChange}
                />
                <LabelWrapper><LabelIcon color={trendColors[0]} /> {ln_t('invoice.numOfInvoice')}</LabelWrapper>
                <LabelWrapper><LabelIcon color={trendColors[1]} /> {ln_t('invoice.numOfCustomer')}</LabelWrapper>
                <LabelWrapper><LabelIcon color={trendColors[2]} isline /> {ln_t('invoice.consumptionAmount')}</LabelWrapper>
              </TrendLabelWrapper>
            </ConfigProvider>
          }}
        />

        <ChartComponent
          title={ln_t('invoice.salesTrendByChannel')}
          data={channel_daily?.data}
          type="line"
          propsFunc={channelChartProps}
          wrapperProps={{ fullwidth: true }}
          innerProps={{ borderless: true }}
          chartState={channelState}
          customLabel={{
            position: 'down',
            component: <ConfigProvider theme={{ token: { colorPrimary: 'rgb(71, 182, 175)', colorPrimaryHover: 'rgba(71, 182, 175,.8)' } }}>
              <ChannelLabelWrapper>
                <Checkbox.Group
                  value={channelState?.selected}
                  options={[
                    { value: 'all', label: ln_t('invoice.selectAll') },
                    ...channelLabels?.map(i => ({ value: i, label: i, style: { color: channelColorMapping[i] } }))
                  ]}
                  onChange={handleChannelCheckboxChange}
                />
              </ChannelLabelWrapper>
            </ConfigProvider>
          }}
        />

        <ChartComponent
          title={ln_t('invoice.channelShareRanking')}
          data={channel_share?.data}
          type="doughnut"
          propsFunc={channelShareChartProps}
          wrapperProps={{ fullwidth: true }}
          innerProps={{ borderless: true }}
        />
      </WhiteWrapper>
    </DashboardWrapper>
  </>
}

export default InvoiceDashboard
